import { useTranslation } from 'react-i18next'
import {
  mdiTimelineTextOutline,
  mdiPlusCircleOutline,
  mdiSwapHorizontal,
  mdiCalendarClock,
  mdiAccountHardHatOutline,
  mdiPaperclip,
  mdiPaperclipOff,
  mdiCurrencyEur,
  mdiCommentOutline,
  mdiCircleSmall,
  mdiEmailFastOutline,
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiCalendarCheckOutline,
  mdiClipboardCheckOutline,
  mdiSealVariant,
  mdiAlertOctagonOutline,
  mdiCogOutline,
  mdiCogPlayOutline,
  mdiCallMerge,
} from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Separator } from '../ui/separator'
import { useFormatters } from '../../lib/format'
import type { InterventionEvent, InterventionEventType } from '../../hooks/useInterventions'
import { InterventionSectionTitle } from './InterventionDocumentsSection'
import { EmptyState } from '../ui/empty-state'

// --- Timeline (§2) — shared between InterventionDetailDrawer and InterventionPage ---
export function eventLabel(e: InterventionEvent, t: (k: string, o?: Record<string, unknown>) => string): string {
  const p = (e.payload || {}) as Record<string, unknown>
  switch (e.event_type) {
    case 'created':
      return t('interventions.event.created', { defaultValue: 'Intervention créée' })
    case 'status_changed':
      return t('interventions.event.status_changed', { defaultValue: `Statut : ${p.from} → ${p.to}`, from: p.from, to: p.to })
    case 'rescheduled':
      return t('interventions.event.rescheduled', { defaultValue: 'Replanifiée' })
    case 'provider_assigned':
      return t('interventions.event.provider_assigned', { defaultValue: 'Prestataire assigné' })
    case 'document_attached':
      return t('interventions.event.document_attached', { defaultValue: 'Document attaché' })
    case 'document_detached':
      return t('interventions.event.document_detached', { defaultValue: 'Document détaché' })
    case 'expense_linked':
      return t('interventions.event.expense_linked', { defaultValue: 'Dépense liée' })
    // Work-order loop (spec 2026-08-01-01): the timeline becomes the narrative
    // of an ordre de service, not just a log of field edits.
    case 'os_sent':
      return t('interventions.event.os_sent', { defaultValue: 'Ordre de service envoyé' })
    case 'os_accepted':
      return t('interventions.event.os_accepted', { defaultValue: 'Ordre de service accepté par le prestataire' })
    case 'os_declined':
      return t('interventions.event.os_declined', { defaultValue: 'Ordre de service décliné', reason: p.reason })
    case 'date_proposed':
      return t('interventions.event.date_proposed', { defaultValue: "Date d'intervention confirmée" })
    case 'work_submitted':
      return t('interventions.event.work_submitted', { defaultValue: 'Travaux déclarés réalisés par le prestataire' })
    case 'work_validated':
      return t('interventions.event.work_validated', { defaultValue: 'Travaux validés' })
    case 'work_disputed':
      return t('interventions.event.work_disputed', { defaultValue: 'Travaux contestés', reason: p.reason })
    case 'equipment_selected':
      return t('interventions.event.equipment_selected', { defaultValue: 'Équipement concerné renseigné' })
    case 'equipment_declared':
      return t('interventions.event.equipment_declared', { defaultValue: 'Équipement déclaré par le prestataire', name: p.name })
    case 'equipment_confirmed':
      return t('interventions.event.equipment_confirmed', { defaultValue: 'Équipement confirmé au registre' })
    case 'equipment_rejected':
      return t('interventions.event.equipment_rejected', { defaultValue: 'Équipement déclaré rejeté' })
    case 'equipment_merged':
      return t('interventions.event.equipment_merged', { defaultValue: 'Équipement fusionné avec un existant' })
    default:
      return e.event_type
  }
}

// eventIcon maps each InterventionEventType (see useInterventions.ts) to an
// MDI path. Unknown/future types fall back to a neutral dot — the raw
// event_type string is never rendered to the user.
export function eventIcon(eventType: InterventionEventType | string): string {
  switch (eventType) {
    case 'created':
      return mdiPlusCircleOutline
    case 'status_changed':
      return mdiSwapHorizontal
    case 'rescheduled':
      return mdiCalendarClock
    case 'provider_assigned':
      return mdiAccountHardHatOutline
    case 'document_attached':
      return mdiPaperclip
    case 'document_detached':
      return mdiPaperclipOff
    case 'expense_linked':
      return mdiCurrencyEur
    case 'comment':
      return mdiCommentOutline
    case 'os_sent':
      return mdiEmailFastOutline
    case 'os_accepted':
      return mdiCheckCircleOutline
    case 'os_declined':
      return mdiCloseCircleOutline
    case 'date_proposed':
      return mdiCalendarCheckOutline
    case 'work_submitted':
      return mdiClipboardCheckOutline
    case 'work_validated':
      return mdiSealVariant
    case 'work_disputed':
      return mdiAlertOctagonOutline
    case 'equipment_selected':
      return mdiCogOutline
    case 'equipment_declared':
      return mdiCogPlayOutline
    case 'equipment_confirmed':
      return mdiCheckCircleOutline
    case 'equipment_rejected':
      return mdiCloseCircleOutline
    case 'equipment_merged':
      return mdiCallMerge
    default:
      return mdiCircleSmall
  }
}

export function InterventionTimelineSection({ events, showSeparator = true }: { events: InterventionEvent[]; showSeparator?: boolean }) {
  const { t } = useTranslation('assets')
  const { formatDate } = useFormatters()

  const fmtDateTime = (s?: string | null): string => {
    if (!s) return '—'
    return formatDate(s, { dateStyle: 'medium', timeStyle: 'short' })
  }

  return (
    <div>
      <InterventionSectionTitle icon={mdiTimelineTextOutline} testid="intervention-timeline-title">
        {t('interventions.detail.timeline', { defaultValue: 'Historique' })}
      </InterventionSectionTitle>
      {events.length === 0 ? (
        <EmptyState
          variant="inline"
          className="py-3"
          description={t('interventions.detail.no_events', { defaultValue: 'Aucun évènement.' })}
          data-testid="intervention-timeline-empty"
        />
      ) : (
        <ol className="space-y-2" data-testid="intervention-timeline">
          {events.map((e, idx) => {
            const label = eventLabel(e, t)
            return (
              <li
                key={e.uid}
                className="flex items-start gap-2 text-sm"
                data-testid={`intervention-event-${e.event_type}`}
              >
                {/* Fixed-size icon gutter: constant width keeps every row's
                    label/timestamp aligned on the same x axis, and a thin
                    connector between icons reads as a timeline. */}
                <div className="relative flex shrink-0 flex-col items-center self-stretch">
                  <div
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
                    aria-label={label}
                    title={label}
                  >
                    <AppIcon path={eventIcon(e.event_type)} size="xs" />
                  </div>
                  {idx < events.length - 1 && (
                    <div className="mt-1 w-px flex-1 bg-border" aria-hidden="true" />
                  )}
                </div>
                <div className="min-w-0 flex-1 pb-2">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                    <span className="font-medium text-foreground">{label}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">{fmtDateTime(e.created_at)}</span>
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
      )}
      {showSeparator && <Separator className="mt-4" />}
    </div>
  )
}
